<!--
 * 文件功能：应用根组件（英语单词消消乐独立版）
 * 输入参数：无（接收路由渲染各页面）
 * 输出参数：无（输出路由视图 + 路由切换顶部进度条，全局动森渐变背景由 global.css 提供）
 * 返回值：无（Vue SFC 组件）
 * 说明：
 *   - 游戏模块自带底部 Tab 导航（EnglishGamePage），故根组件不再渲染原项目的
 *     顶部导航栏与页脚，保持独立项目的简洁结构；
 *   - 路由切换时在页面顶部显示动森黄色进度条（beforeEach 启动、afterEach 完成），
 *     为懒加载页面提供即时视觉反馈。
 -->
<template>
  <router-view v-slot="{ Component }">
    <transition name="page-fade" mode="out-in">
      <component :is="Component" />
    </transition>
  </router-view>

  <!-- 路由切换顶部进度条（动森黄色） -->
  <div v-show="progressVisible" class="route-progress">
    <div class="route-progress__bar" :style="{ width: progressValue + '%' }"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()

/** 进度条数值（0~100） */
const progressValue = ref(0)

/** 进度条是否可见 */
const progressVisible = ref(false)

/** 进度条增长定时器句柄 */
let progressTimer: number | null = null

/** 进度条收尾延时句柄 */
let progressHideTimer: number | null = null

/**
 * 函数功能：启动路由进度条（从 8% 开始，逐步逼近 90% 模拟加载感）
 * 输入参数：无
 * 输出参数：无（更新 progressValue / progressVisible）
 * 返回值：void
 */
function startProgress(): void {
  if (progressHideTimer) {
    window.clearTimeout(progressHideTimer)
    progressHideTimer = null
  }
  if (progressTimer) window.clearInterval(progressTimer)
  progressVisible.value = true
  progressValue.value = 8
  progressTimer = window.setInterval(() => {
    // 越接近 90% 增长越慢，营造渐进加载效果
    progressValue.value = Math.min(90, progressValue.value + Math.max(1, (90 - progressValue.value) * 0.12))
  }, 120)
}

/**
 * 函数功能：完成路由进度条（冲到 100% 后淡出）
 * 输入参数：无
 * 输出参数：无（更新 progressValue / progressVisible）
 * 返回值：void
 */
function finishProgress(): void {
  if (progressTimer) {
    window.clearInterval(progressTimer)
    progressTimer = null
  }
  if (!progressVisible.value) return
  progressValue.value = 100
  progressHideTimer = window.setTimeout(() => {
    progressVisible.value = false
  }, 350)
}

// 路由钩子：切换开始启动进度条，切换完成（含懒加载解析）收尾
router.beforeEach(() => {
  startProgress()
})
router.afterEach(() => {
  finishProgress()
})

// 首次挂载确保进度条归零
onMounted(() => {
  progressVisible.value = false
})
</script>

<style>
/* ===== 路由切换顶部进度条（动森黄色，fixed 顶部） ===== */
.route-progress {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  z-index: 3000;
  pointer-events: none;
  background: transparent;
}

.route-progress__bar {
  height: 100%;
  background: linear-gradient(90deg, #ffcc00, #ffb800);
  border-radius: 0 50px 50px 0;
  box-shadow: 0 1px 6px rgba(255, 184, 0, 0.55);
  transition: width 0.25s ease;
}

/* ===== 路由切换淡入淡出过渡 ===== */
.page-fade-enter-active,
.page-fade-leave-active {
  transition: opacity 0.18s ease;
}

.page-fade-enter-from,
.page-fade-leave-to {
  opacity: 0;
}
</style>
